import apiClient from './api';
import { validateRequiredParams } from '@/utils/apiUtils';
import type {
  ShareOfVoiceBaseParams,
  ShareOfVoiceCalendarParams,
  ShareOfVoiceMarket,
  ShareOfVoiceCalendarResponse,
  ShareOfVoiceMonthlyParams,
  ShareOfVoiceMonthlyResponse,
  ShareOfVoiceParams,
  ShareOfVoiceResponse,
} from '@/types/shareOfVoice.types';

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Mirrors the both-or-neither date guard: a malformed month fails here rather than as an
 * opaque 422 from the BFF. Shared by the two month-scoped endpoints so they cannot drift.
 */
const assertShareOfVoiceMonth = (month: string): string => {
  const trimmed = month.trim();
  if (!MONTH_PATTERN.test(trimmed)) {
    throw new Error('Share of Voice requires month as YYYY-MM.');
  }
  return trimmed;
};

const trimmedIds = (ids?: string[]): string[] => (ids ?? []).map(id => id.trim()).filter(Boolean);

/** Everything the two SOV endpoints share. Only the date control differs. */
const buildShareOfVoiceQueryParams = (params: ShareOfVoiceBaseParams): Record<string, unknown> => {
  const queryParams: Record<string, unknown> = {};

  const targetCountry = params.target_country?.trim().toUpperCase();
  if (targetCountry) {
    queryParams.target_country = targetCountry;
  }

  if (params.ids_competidores?.length) {
    queryParams.ids_competidores = params.ids_competidores;
  }

  const verticalCode = params.vertical_code?.trim();
  if (verticalCode) {
    queryParams.vertical_code = verticalCode;
  }

  const platform = params.platform?.trim();
  if (platform) {
    queryParams.platform = platform;
  }

  const categoryCode = params.category_code?.trim();
  if (categoryCode) {
    queryParams.category_code = categoryCode;
  }

  const segmentCode = params.segment_code?.trim();
  if (segmentCode) {
    queryParams.segment_code = segmentCode;
  }

  const categoryIds = trimmedIds(params.category_ids);
  if (categoryIds.length > 0) {
    queryParams.category_ids = categoryIds;
  }

  const segmentIds = trimmedIds(params.segment_ids);
  if (segmentIds.length > 0) {
    queryParams.segment_ids = segmentIds;
  }

  if (params.publicado_por_otra_cuenta === true || params.publicado_por_otra_cuenta === false) {
    queryParams.publicado_por_otra_cuenta = params.publicado_por_otra_cuenta;
  }

  if (params.metric) {
    queryParams.metric = params.metric;
  }

  return queryParams;
};

export const getShareOfVoice = async (
  params: ShareOfVoiceParams
): Promise<ShareOfVoiceResponse> => {
  validateRequiredParams(params, ['tenant_code']);

  const { tenant_code, start_date, end_date, ...rest } = params;

  const queryParams = buildShareOfVoiceQueryParams(rest as ShareOfVoiceBaseParams);

  if (start_date && end_date) {
    queryParams.start_date = start_date;
    queryParams.end_date = end_date;
  } else if (start_date || end_date) {
    throw new Error('Share of Voice requires both start_date and end_date, or neither.');
  }

  const response = await apiClient.get<ShareOfVoiceResponse>(
    `/analytics/${encodeURIComponent(tenant_code)}/share-of-voice`,
    { params: queryParams }
  );

  const body = response.data;
  if (!body || typeof body !== 'object') {
    return { platforms: [] };
  }

  return {
    platforms: Array.isArray(body.platforms) ? body.platforms : [],
  };
};

export const getShareOfVoiceMonthly = async (
  params: ShareOfVoiceMonthlyParams
): Promise<ShareOfVoiceMonthlyResponse> => {
  validateRequiredParams(params, ['tenant_code']);

  const { tenant_code, month, ...rest } = params;

  const queryParams = buildShareOfVoiceQueryParams(rest as ShareOfVoiceBaseParams);

  if (month !== undefined) {
    queryParams.month = assertShareOfVoiceMonth(month);
  }

  const response = await apiClient.get<ShareOfVoiceMonthlyResponse>(
    `/analytics/${encodeURIComponent(tenant_code)}/share-of-voice/monthly`,
    { params: queryParams }
  );

  const body = response.data;
  if (!body || typeof body !== 'object') {
    return { periodo_mes: '', platforms: [] };
  }

  return {
    periodo_mes: typeof body.periodo_mes === 'string' ? body.periodo_mes : '',
    platforms: Array.isArray(body.platforms) ? body.platforms : [],
  };
};

export const getShareOfVoiceCalendar = async (
  params: ShareOfVoiceCalendarParams
): Promise<ShareOfVoiceCalendarResponse> => {
  validateRequiredParams(params, ['tenant_code']);

  const { tenant_code, month, ...rest } = params;

  const queryParams = buildShareOfVoiceQueryParams(rest as ShareOfVoiceBaseParams);
  queryParams.month = assertShareOfVoiceMonth(month);

  const response = await apiClient.get<ShareOfVoiceCalendarResponse>(
    `/analytics/${encodeURIComponent(tenant_code)}/share-of-voice/calendar`,
    { params: queryParams }
  );

  const body = response.data;
  if (!body || typeof body !== 'object') {
    return { periodo_mes: '', days: [] };
  }

  return {
    periodo_mes: typeof body.periodo_mes === 'string' ? body.periodo_mes : '',
    days: Array.isArray(body.days) ? body.days : [],
  };
};

/**
 * The tenant's market options: a country, optionally annexed with a brand vertical.
 *
 * Defensive on every field, like the countries call it replaces: a malformed row must
 * drop out rather than render a market with no label or, worse, one whose
 * `country_code` would 422 the whole section when sent as `target_country`.
 */
export const getShareOfVoiceMarkets = async (tenantCode: string): Promise<ShareOfVoiceMarket[]> => {
  const response = await apiClient.get<ShareOfVoiceMarket[]>(
    `/analytics/${encodeURIComponent(tenantCode)}/share-of-voice/markets`
  );

  const body = response.data;
  if (!Array.isArray(body)) return [];

  return body.reduce<ShareOfVoiceMarket[]>((markets, raw) => {
    const code = (raw?.code ?? '').trim().toUpperCase();
    const countryCode = (raw?.country_code ?? '').trim().toUpperCase();
    if (!code || !countryCode) return markets;

    const verticalCode = (raw?.vertical?.code ?? '').trim();
    const verticalName = (raw?.vertical?.name ?? '').trim();

    markets.push({
      code,
      country_code: countryCode,
      vertical: verticalCode ? { code: verticalCode, name: verticalName || verticalCode } : null,
      is_global: Boolean(raw?.is_global),
      account_group_codes: Array.isArray(raw?.account_group_codes)
        ? raw.account_group_codes.map(value => `${value}`.trim()).filter(Boolean)
        : [],
    });
    return markets;
  }, []);
};
